import React from 'react';
import { getActiveAttributeValues } from '../../utils/productAttributeSelection';

export default function ProductAttributeFields({
    definitions = [],
    selections = {},
    onChange,
    disabled = false,
    prefix = 'pd',
    fieldClassName = 'config-group',
    labelClassName,
    showSectionTitle = false,
    sectionTitle = 'Options',
    allowPriceInput = false,
    forceCheckbox = false,
}) {
    if (definitions.length === 0) return null;

    const inputClass = `${prefix}-attr-input`;
    const selectClass = `${prefix}-attr-select`;
    const checkboxGroupClass = `${prefix}-attr-checkbox-group`;
    const checkboxLabelClass = `${prefix}-attr-checkbox-label`;
    const radioGroupClass = `${prefix}-attr-radio-group`;
    const radioLabelClass = `${prefix}-attr-radio-label`;
    const priceClass = prefix === 'mp' ? 'mp-price-input' : `${prefix}-attr-price`;
    const emptyClass = prefix === 'mp' ? 'mp-no-colors' : `${prefix}-no-options`;
    const labelClass = labelClassName === null
        ? undefined
        : (labelClassName || (prefix === 'mp' ? undefined : 'pd-attribute-label'));

    const handleChange = (name, value) => {
        if (disabled || typeof onChange !== 'function') return;
        onChange(name, value);
    };

    const handlePriceChange = (name, value, price) => {
        if (disabled || typeof onChange !== 'function') return;
        const currentVal = selections[name];
        let newVal;
        if (Array.isArray(currentVal)) {
            const existingIdx = currentVal.findIndex(v => (typeof v === 'object' ? v.value : v) === value);
            if (existingIdx >= 0) {
                newVal = [...currentVal];
                newVal[existingIdx] = { value, price: Number(price) || 0 };
            } else {
                newVal = [...currentVal, { value, price: Number(price) || 0 }];
            }
        } else {
            newVal = { value, price: Number(price) || 0 };
        }
        onChange(name, newVal);
    };

    return (
        <>
            {showSectionTitle && <div className={`${prefix === 'mp' ? 'mp-field-section-title' : 'pd-attributes-title'}`}>{sectionTitle}</div>}
            {definitions.map((attribute) => {
                const attrValue = selections[attribute.name] ?? '';
                const activeValues = getActiveAttributeValues(attribute);
                const isText = !forceCheckbox && attribute.type === 'text';
                const isColor = !forceCheckbox && attribute.type === 'color';
                const isCheckbox = forceCheckbox || attribute.type === 'checkbox';
                const isRadio = !forceCheckbox && attribute.type === 'radio';

                return (
                    <div key={attribute.id || attribute.name} className={fieldClassName}>
                        <label className={labelClass}>{attribute.name}</label>
                        {isText ? (
                            <input
                                type="text"
                                className={inputClass}
                                value={typeof attrValue === 'string' ? attrValue : ''}
                                onChange={(e) => handleChange(attribute.name, e.target.value)}
                                placeholder={`Enter ${attribute.name.toLowerCase()}...`}
                                disabled={disabled}
                            />
                        ) : isColor ? (
                            <div className={radioGroupClass} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {activeValues.map((value) => {
                                    const currentVal = typeof attrValue === 'object' && attrValue !== null ? attrValue.value : attrValue;
                                    const currentPrice = typeof attrValue === 'object' && attrValue !== null ? (attrValue.price || 0) : 0;
                                    const isSelected = currentVal === value.name;
                                    return (
                                        <label key={value.name} className={radioLabelClass} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: '4px', margin: 0 }}>
                                            <input
                                                type="radio"
                                                name={`attr-${attribute.id || attribute.name}`}
                                                value={value.name}
                                                checked={isSelected}
                                                disabled={disabled}
                                                style={{ display: 'none' }}
                                                onChange={() => {
                                                    handleChange(attribute.name, allowPriceInput ? { value: value.name, price: currentPrice } : value.name);
                                                }}
                                            />
                                            <div style={{ width: '32px', height: '32px', backgroundColor: value.name, border: isSelected ? '2px solid #000' : '1px solid #ccc', borderRadius: '4px', boxShadow: isSelected ? '0 0 0 2px #fff inset' : 'none' }}></div>
                                            {allowPriceInput && (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={priceClass}
                                                    value={isSelected ? currentPrice : ''}
                                                    onChange={(e) => {
                                                        if (isSelected) {
                                                            handleChange(attribute.name, { value: value.name, price: Number(e.target.value) || 0 });
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="₱0"
                                                />
                                            )}
                                        </label>
                                    );
                                })}
                                {activeValues.length === 0 && <span className={emptyClass}>No active values</span>}
                            </div>
                        ) : isCheckbox ? (
                            <div className={checkboxGroupClass}>
                                {activeValues.map((value) => {
                                    const arr = Array.isArray(attrValue) ? attrValue : [];
                                    const checked = arr.some((v) => (typeof v === 'object' ? v.value : v) === value.name);
                                    const itemPrice = arr.find(v => (typeof v === 'object' ? v.value : v) === value.name);
                                    const currentPrice = typeof itemPrice === 'object' ? (itemPrice.price || 0) : 0;
                                    return (
                                        <label key={value.name} className={checkboxLabelClass}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={() => {
                                                    const next = checked
                                                        ? arr.filter((v) => (typeof v === 'object' ? v.value : v) !== value.name)
                                                        : [...arr, { value: value.name, price: 0 }];
                                                    handleChange(attribute.name, next);
                                                }}
                                            />
                                            <span>{value.name}</span>
                                            {allowPriceInput && (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={priceClass}
                                                    value={checked ? currentPrice : ''}
                                                    onChange={(e) => handlePriceChange(attribute.name, value.name, e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="₱0"
                                                />
                                            )}
                                        </label>
                                    );
                                })}
                                {activeValues.length === 0 && <span className={emptyClass}>No active values</span>}
                            </div>
                        ) : isRadio ? (
                            <div className={radioGroupClass}>
                                {activeValues.map((value) => {
                                    const currentVal = typeof attrValue === 'object' && attrValue !== null ? attrValue.value : attrValue;
                                    const currentPrice = typeof attrValue === 'object' && attrValue !== null ? (attrValue.price || 0) : 0;
                                    return (
                                        <label key={value.name} className={radioLabelClass}>
                                            <input
                                                type="radio"
                                                name={`attr-${attribute.id || attribute.name}`}
                                                value={value.name}
                                                checked={currentVal === value.name}
                                                disabled={disabled}
                                                onChange={() => {
                                                    handleChange(attribute.name, allowPriceInput ? { value: value.name, price: currentPrice } : value.name);
                                                }}
                                            />
                                            <span>{value.name}</span>
                                            {allowPriceInput && (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={priceClass}
                                                    value={currentVal === value.name ? currentPrice : ''}
                                                    onChange={(e) => {
                                                        if (currentVal === value.name) {
                                                            handleChange(attribute.name, { value: value.name, price: Number(e.target.value) || 0 });
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="₱0"
                                                />
                                            )}
                                        </label>
                                    );
                                })}
                                {activeValues.length === 0 && <span className={emptyClass}>No active values</span>}
                            </div>
                        ) : (
                            <div className={`${selectClass}-wrapper`}>
                                <select
                                    className={selectClass}
                                    value={typeof attrValue === 'object' ? attrValue?.value ?? '' : attrValue}
                                    disabled={disabled}
                                    onChange={(e) => {
                                        const price = typeof attrValue === 'object' && attrValue !== null ? attrValue.price : 0;
                                        handleChange(attribute.name, allowPriceInput ? { value: e.target.value, price: Number(price) || 0 } : e.target.value);
                                    }}
                                >
                                    <option value="">Select {attribute.name.toLowerCase()}...</option>
                                    {activeValues.map((value) => (
                                        <option key={value.name} value={value.name}>
                                            {value.name}
                                        </option>
                                    ))}
                                </select>
                                {allowPriceInput && typeof attrValue === 'object' && attrValue !== null && attrValue.value && (
                                    <input
                                        type="number"
                                        min="0"
                                        className={priceClass}
                                        value={attrValue.price || 0}
                                        onChange={(e) => {
                                            handleChange(attribute.name, { value: attrValue.value, price: Number(e.target.value) || 0 });
                                        }}
                                        placeholder="₱0"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
}